import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { shallow } from "zustand/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import Input from "../../components/form/Input";
import Spinner from "../../components/misc/Spinner";
import Modal, { ModalBody, ModalFooter, ModalHeader } from "../../components/modal/Modal";
import AsyncCreatableSelectWrapper from "../../components/select/AsyncCreatableSelectWrapper";
import { DEV_MODE } from "../../constants";
import * as itemGroupApi from "../../services/item_groups";
import { Role, useAuthStore } from "../../stores/auth";
import { useItemGroupStore } from "../../stores/item_groups";
import { ITEM_CHARACTERISTIC, useItemStore } from "../../stores/items";
import { useToastStore } from "../../stores/toast";

const createInitialValues = (me, item, isAddMode) => ({
  item: {
    id: item?.id || "",
    number: item?.number || "",
    name: item?.name || "",
    description: item?.description || "",
    highlight: item?.highlight || false,
    characteristic: item?.characteristic || ITEM_CHARACTERISTIC.DIFFERENT,
    inspect_every_x_months: item?.inspect_every_x_months || "",
    group_ids: item?.groups?.map((g) => g.id) || [],
  },
});

const createValidationSchema = (t) =>
  Yup.object().shape({
    item: Yup.object().shape({
      inspect_every_x_months: Yup.number()
        .positive(t("errors.types.number.positive"))
        .nullable()
        .optional()
        .typeError(t("errors.types.number.number")),
    }),
  });

function ItemAddEdit() {
  const { t } = useTranslation();
  const params = useParams();
  const itemId = Number.parseInt(params.itemId);
  const isAddMode = !itemId;
  const navigate = useNavigate();
  const addToast = useToastStore((state) => state.addToast);

  const [me, hasRole] = useAuthStore((state) => [state.me, state.hasRole], shallow);
  const canView = hasRole(Role.ADMINISTRATOR) || hasRole(Role.MANAGER);
  const canEdit = hasRole(Role.ADMINISTRATOR) || hasRole(Role.MANAGER);

  const [byIds, , getById, createOne, updateOne] = useItemStore(
    (state) => [state.byIds, state.allIds, state.getById, state.createOne, state.updateOne],
    shallow,
  );

  const [groupByIds, groupAllIds, fetchGroups, createGroup, updateGroup] = useItemGroupStore(
    (state) => [state.byIds, state.allIds, state.fetchSome, state.createOne, state.updateOne],
    shallow,
  );

  const item = Number.isInteger(itemId) ? byIds[itemId] : null;

  useEffect(() => {
    fetchGroups({ limit: 1000 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (itemId) {
      getById({ id: itemId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const [renameModal, setRenameModal] = useState(null);

  const loadGroupOptions = useCallback(async (inputValue) => {
    const { data } = await itemGroupApi.getSome({ q: inputValue, limit: 50 });
    return data.map((g) => ({ value: g.id, label: g.name }));
  }, []);

  const handleRename = async () => {
    const { error } = await updateGroup({
      id: renameModal.id,
      name: renameModal.name,
    });
    if (!error) {
      setRenameModal(null);
    }
  };

  const handleSubmit = async (values, { setErrors }) => {
    const payload = {
      number: values.item.number,
      name: values.item.name,
      description: values.item.description,
      highlight: values.item.highlight,
      characteristic: values.item.characteristic,
      inspect_every_x_months: values.item.inspect_every_x_months || null,
      group_ids: values.item.group_ids,
    };
    const { error } = isAddMode ? await createOne(payload) : await updateOne({ id: itemId, ...payload });

    if (error) {
      const msg = error?.detail?.[0]?.t || error?.detail;
      addToast("error", msg ? t(msg) : JSON.stringify(error));
    } else {
      addToast("success", t("common.saved"));
    }
  };

  if (Number.isInteger(itemId) && !item) {
    return <Spinner />;
  }

  if (!canView) {
    navigate("/");
  }

  const canSubmit = canEdit;

  return (
    <div>
      <Breadcrumb
        items={[
          { label: t("items.items"), to: "/items" },
          {
            label: isAddMode ? t("menu.add") : `${t("common.number_short")} ${item.number}`,
          },
        ]}
      />

      {!canSubmit && <div className="alert alert-danger">{t("permissions.can_not_edit")}</div>}

      <Formik
        initialValues={createInitialValues(me, item, isAddMode)}
        validationSchema={createValidationSchema(t)}
        validateOnChange={true}
        validateOnBlur={false}
        enableReinitialize={false}
        validateOnMount={false}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setValues,
          setFieldValue,
          /* and other goodies */
        }) => (
          <Form>
            <div className="row">
              <div className="col">
                <div className="card border-0 mb-4">
                  <div className="card-header h6 mb-0 bg-none p-3">
                    <i className="fa fa-cog fa-lg fa-fw text-dark text-opacity-50 me-1"></i> {t("items.item")}
                  </div>
                  <div className="card-body">
                    <div className="row mb-3 gx-3">
                      <div className="col-md-3 mb-3 mb-md-0">
                        <label className="form-label" htmlFor="item.number">
                          {t("items.number")}
                        </label>
                        <Field
                          component={Input}
                          name="item.number"
                          className="form-control fs-13px h-45px"
                          disabled={!canEdit}
                        />
                        <ErrorMessage
                          name="item.number"
                          render={(error) => (
                            <div className="invalid-feedback">
                              {Array.isArray(error) ? (
                                error.map((e, i) => <div key={i}>{t(e.t)}</div>)
                              ) : (
                                <div>{t(error)}</div>
                              )}
                            </div>
                          )}
                        />
                      </div>
                      <div className="col-md">
                        <label className="form-label" htmlFor="item.name">
                          {t("items.name")}
                        </label>
                        <Field
                          component={Input}
                          name="item.name"
                          className="form-control fs-13px h-45px"
                          disabled={!canEdit}
                        />
                        <ErrorMessage
                          name="item.name"
                          render={(msg) => <div className="invalid-feedback">{t(msg)}</div>}
                        />
                      </div>
                    </div>
                    <div className="row mb-3 gx-3">
                      <div className="col-md-3">
                        <label className="form-label" htmlFor="item.inspect_every_x_months">
                          {t("items.inspect_every_x_months")}
                        </label>
                        <Field
                          component={Input}
                          name="item.inspect_every_x_months"
                          className="form-control fs-13px h-45px"
                          disabled={!canEdit}
                        ></Field>
                        <ErrorMessage
                          name="item.inspect_every_x_months"
                          render={(msg) => <div className="invalid-feedback">{t(msg)}</div>}
                        />
                      </div>
                      <div className="col-md">
                        <label className="form-label">{t("items.groups.group")}</label>
                        <div className="d-flex gap-2">
                          <div className="flex-grow-1">
                            <AsyncCreatableSelectWrapper
                              defaultOptions
                              loadOptions={loadGroupOptions}
                              value={
                                values.item.group_ids.length > 0 && groupByIds[values.item.group_ids[0]]
                                  ? {
                                      value: values.item.group_ids[0],
                                      label: groupByIds[values.item.group_ids[0]].name,
                                    }
                                  : null
                              }
                              onChange={(option) => setFieldValue("item.group_ids", option ? [option.value] : [])}
                              onCreateOption={async (inputValue) => {
                                const { data } = await createGroup({
                                  name: inputValue,
                                });
                                if (data) {
                                  setFieldValue("item.group_ids", [data.id]);
                                }
                              }}
                              isClearable
                              isDisabled={!canEdit}
                              placeholder="—"
                            />
                          </div>
                          {values.item.group_ids.length > 0 && groupByIds[values.item.group_ids[0]] && canEdit && (
                            <button
                              type="button"
                              className="btn btn-outline-secondary h-45px"
                              onClick={() => {
                                const g = groupByIds[values.item.group_ids[0]];
                                setRenameModal({ id: g.id, name: g.name });
                              }}
                            >
                              <i className="fa fa-pencil"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="row mb-3 gx-3">
                      <div className="col">
                        <label className="form-label" htmlFor="item.description">
                          {t("items.description")}
                        </label>
                        <div className={`form-control p-0 overflow-hidden ${!canEdit ? "bg-light" : ""}`}>
                          <ReactQuill
                            value={values.item.description}
                            onChange={(text) => setFieldValue("item.description", text)}
                            className="border-0 h-200px mb-40px"
                            readOnly={!canEdit}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="row mb-3 gx-3">
                      <div className="col-12 mb-2">
                        <Field
                          type="checkbox"
                          name="item.highlight"
                          className="form-check-input me-2"
                          disabled={!canEdit}
                        />
                        <label className="form-label" htmlFor="item.highlight">
                          {t("items.highlight")}
                        </label>
                      </div>
                      <div className="col-12">
                        {Object.values(ITEM_CHARACTERISTIC).map((v) => (
                          <div className="form-check" key={v}>
                            <Field
                              type="radio"
                              name="item.characteristic"
                              value={v}
                              className="form-check-input"
                              disabled={!canEdit}
                            />
                            <label className="form-label form-check-label" htmlFor="item.characteristic">
                              {t(`items.characteristics.${v}`)}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card border-0 mb-4">
                  <div className="card-header h6 mb-0 bg-none p-3"></div>
                  <div className="card-body">
                    <div className="btn btn-group d-flex">
                      <button className="btn btn-success w-100" disabled={isSubmitting || !canSubmit}>
                        {t("menu.submit")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {DEV_MODE && (
              <pre
                className="m-t-xl"
                style={{
                  fontSize: "1rem",
                  padding: ".25rem .5rem",
                  overflowX: "scroll",
                }}
              >
                VALUES: {JSON.stringify(values, null, 2)}
                <br />
                ERRORS: {JSON.stringify(errors, null, 2)}
              </pre>
            )}
          </Form>
        )}
      </Formik>

      {renameModal && (
        <Modal>
          <ModalHeader>
            <h5 className="modal-title">{t("items.groups.rename")}</h5>
          </ModalHeader>
          <ModalBody>
            <input
              type="text"
              className="form-control fs-13px h-45px"
              value={renameModal.name}
              onChange={(e) => setRenameModal({ ...renameModal, name: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleRename();
                }
              }}
              autoFocus
            />
          </ModalBody>
          <ModalFooter>
            <button type="button" className="btn btn-default" onClick={() => setRenameModal(null)}>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="btn btn-success"
              disabled={!renameModal.name.trim()}
              onClick={handleRename}
            >
              {t("menu.submit")}
            </button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}

export default ItemAddEdit;
